// -=-=-=-=-=-=-=
// ↓↓↓ PRODUCT RECOMMENDATION FROM SEARCH AND DISCOVERY ↓↓↓
// AFTER GETTING DATA WE'RE INITIALISING SLIDER LIBRARY
// -=-=-=-=-=-=-=

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".product_recommendation")) {
          this.classList.add("product-recommendations--loaded");
          waitForSplideManager(() => {
            const slider = this.querySelector(".splide");
            if (slider) {
              const safeId = this.dataset.sectionId.replace(/[- .]/g, "_");
              const configFn = window[`getSplideConfig_${safeId}`];

              if (typeof configFn === "function") {
                SplideManager.initSlider(slider, configFn(slider));
              } else {
                console.warn(
                  `[Splide] Missing config function: getSplideConfig_${safeId}`
                );
              }
            }
          });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

// -=-=-=-=-=-=-=
// ↓↓↓ SPLIDE JS SLIDER FOR PRODUCT CARDS ↓↓↓
// SPLIDE JS LIBRARY REQUIRED
// -=-=-=-=-=-=-=

document.addEventListener("DOMContentLoaded", function () {
  // Always use the shared initializer
  initializeSplideCarouselsOnProductCards();

  // Watch for dynamically added product cards
  observeProductCardInsertions();
});

const splideInstances = new WeakMap();
let mutationTimeout;

function initializeSplideCarouselsOnProductCards(scope = document) {
  const carousels = [];

  // If scope itself is a carousel container, add it directly
  if (scope.classList && scope.classList.contains("product-card-carousel")) {
    carousels.push(scope);
  }
  // Otherwise query inside scope
  else {
    carousels.push(...scope.querySelectorAll(".product-card-carousel"));
  }

  if (!carousels.length) return;

  carousels.forEach((el) => {
    if (splideInstances.has(el)) return; // already initialized

    const instance = new Splide(el, {
      type: "loop",
      perPage: 1,
      pagination: false,
      arrows: true,
      lazyLoad: "nearby",
    });

    instance.mount();
    splideInstances.set(el, instance);
  });
}

function observeProductCardInsertions() {
  const observerTarget =
    document.querySelector("#ProductGrid") ||
    document.querySelector(".collection") ||
    document.body;

  const observer = new MutationObserver((mutationsList) => {
    // Debounce updates: batch mutations together
    if (mutationTimeout) clearTimeout(mutationTimeout);

    mutationTimeout = setTimeout(() => {
      const nodesToInitialize = new Set();

      mutationsList.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;

            if (
              node.classList.contains("product-card-carousel") ||
              node.querySelector(".product-card-carousel")
            ) {
              nodesToInitialize.add(node);
            }
          });
        }
      });

      // Initialize each node once
      nodesToInitialize.forEach((node) =>
        initializeSplideCarouselsOnProductCards(node)
      );
    }, 50); // wait 50ms before processing mutations to batch them
  });

  observer.observe(observerTarget, {
    childList: true,
    subtree: true,
  });
}

// Initial call on page load to initialize existing carousels
initializeSplideCarouselsOnProductCards();

// Start observing
observeProductCardInsertions();
